import { Box, Button, Flex, Heading, Input, InputGroup, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { DateRangeFilterFields } from '@/components/DateRangeFilterFields';
import { DateRangeChangeHandler } from '@/components/DateRangeFilterFields/types';
import {
  PredictionsInteractivity,
  PredictionsStatus,
  translatePredictionsInteractivity,
  translatePredictionsStatus,
} from '@/model/predictions';
import { PredictionsFilters } from '../../types';
import { useTranslation } from 'react-i18next';
import { Club } from '@/model';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface PredictionsBoxProps {
  filtersForm: UseFormReturn<PredictionsFilters>;
  forceRefetch: () => void;
  clubs: Club[];
}

export const PredictionsFiltersBox = ({ clubs, filtersForm }: PredictionsBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const dateValueControl = useController({
    name: 'dateValue',
    control,
  });
  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  const handleRangeChange: DateRangeChangeHandler = (startDate, endDate) => {
    startDateControl.field.onChange(startDate);
    endDateControl.field.onChange(endDate);
  };

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'column' }} gap='small' rowGap='micro'>
        <Flex direction={{ base: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} wrap='wrap' gap={6}>
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='20%' />

          <InputGroup w={{ base: '100%', md: 'fit-content' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_poll_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('value'))} />
          </InputGroup>

          <InputGroup w={{ base: '100%', md: '130px' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_status_label')}
            </Text>
            <SelectField {...register('status')}>
              <option value=''>
                {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_status_all')}
              </option>
              <option value={PredictionsStatus.PUBLISHED}>{translatePredictionsStatus(PredictionsStatus.PUBLISHED)}</option>
              <option value={PredictionsStatus.NOT_PUBLISHED}>
                {translatePredictionsStatus(PredictionsStatus.NOT_PUBLISHED)}
              </option>
              <option value={PredictionsStatus.DRAFT}>{translatePredictionsStatus(PredictionsStatus.DRAFT)}</option>
            </SelectField>
          </InputGroup>

          <InputGroup w={{ base: '100%', md: '130px' }} display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_interactivity_label')}
            </Text>
            <SelectField {...register('interactivity')}>
              <option value=''>
                {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_interactivity_all')}
              </option>
              <option value={PredictionsInteractivity.ENABLED}>
                {translatePredictionsInteractivity(PredictionsInteractivity.ENABLED)}
              </option>
              <option value={PredictionsInteractivity.CLOSED}>
                {translatePredictionsInteractivity(PredictionsInteractivity.CLOSED)}
              </option>
            </SelectField>
          </InputGroup>

          <DateRangeFilterFields
            onRangeChange={handleRangeChange}
            startDate={startDateControl.field.value}
            endDate={endDateControl.field.value}
            dateValue={dateValueControl.field.value}
            onDateValueChange={dateValueControl.field.onChange}
            inputName='interactive-area-Predictions-date'
            startDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
            endDateRootProps={{ w: { base: '100%', md: 'fit-content' } }}
          />
        </Flex>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_predictions.components.predictions_filters_box.predictions_filters_clear_filters')}
        </Button>
      </Flex>
    </Box>
  );
};
